import { useMemo, useState } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ArrowLeft, Star } from "lucide-react";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { GrCircleInformation } from "react-icons/gr";
import Modal from "@/components/Modal";
import ReportForm from "./ReportForm";

export default function ReviewCards({ reviews, isLoading, error }) {
  const [selectedReview, setSelectedReview] = useState(null);
  const [viewMode, setViewMode] = useState("default");

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading reviews</div>;

  const fearuredReviews = reviews.filter((review) => review.featured === true);
  const recentReviews = [...reviews].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
  const topRatedReviews = [...reviews].sort((a, b) => b.rating - a.rating);

  const reviewGroups = [
    {
      title: "Featured",
      getData: (showAll) =>
        showAll ? fearuredReviews : fearuredReviews.slice(0, 3),
      id: "featured",
    },
    {
      title: "Recently Added",
      getData: (showAll) =>
        showAll ? recentReviews : recentReviews.slice(0, 3),
      id: "recent",
    },
    {
      title: "Top Rated",
      getData: (showAll) =>
        showAll ? topRatedReviews : topRatedReviews.slice(0, 3),
      id: "top-rated",
    },
  ];

  const displayGroups =
    viewMode === "default"
      ? reviewGroups
      : reviewGroups.filter((group) => group.id === viewMode);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-y-auto min-h-96 ">
      {viewMode !== "default" && (
        <button
          className="mb-4 flex items-center gap-1 text-gray-600 hover:text-gray-900"
          onClick={() => setViewMode("default")}
        >
          <ArrowLeft size={16} />
          <span className="text-sm text-gray-500">Back to Default</span>
        </button>
      )}
      <div className="space-y-8">
        {displayGroups.map((group, index) => {
          const isViewAll = viewMode === group.id;
          const reviewsToDisplay = group.getData(isViewAll);
          return (
            <div className="mb-6" key={index}>
              <div className="flex justify-between items-center py-2">
                <h2 className="font-medium text-lg">{group.title}</h2>
                {!isViewAll && (
                  <button
                    onClick={() => setViewMode(group.id)}
                    className="text-sm text-gray-500"
                  >
                    View All
                  </button>
                )}
              </div>
              <div className="flex flex-col">
                {reviewsToDisplay.length > 0 ? (
                  <div className=" flex flex-col items-center md:grid md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 ">
                    {reviewsToDisplay.map((r, idx) => (
                      <Card
                        key={idx}
                        reviewData={r}
                        onClick={() => setSelectedReview(r)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-center items-center  h-96">
                    <p className="text-gray-400">No reviews found</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {selectedReview && (
        <Modal onClose={() => setSelectedReview(null)}>
          <ReviewDetail reviewData={selectedReview} />
        </Modal>
      )}
    </div>
  );
}

const Card = ({ reviewData, onClick }) => {
  const [reportVisible, setReportVisible] = useState(false);
  const [reportFormVisible, setReportFormVisible] = useState(false);
  const toggleReport = () => setReportVisible((prev) => !prev);
  const formattedTimestamp = reviewData?.createdAt
    ? formatDistanceToNow(parseISO(reviewData?.createdAt), { addSuffix: true })
    : "A while ago";

  // console.log("Review Data", reviewData);

  return (
    <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 flex flex-col h-full max-w-96 cursor-pointer">
      <div className="flex items-center justify-between mb-4 ">
        {/* Avatar container with relative positioning */}
        <div className="relative">
          {reviewData?.user?.picture ? (
            <img
              src={reviewData?.user?.picture}
              alt={reviewData?.user?.name}
              className="w-12 h-12 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-lg font-bold text-white">
              {reviewData?.user?.name?.charAt(0)}
            </div>
          )}
          {/* Green status dot */}
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
        </div>

        <div className="ml-4 flex-grow">
          <h2 className="md:text-lg font-bold text-orange-600 truncate">
            {reviewData?.user?.name}
          </h2>
          <p className="text-gray-400 text-xs ">{reviewData?.user?._id}</p>
        </div>
        <div className="relative">
          <BiDotsVerticalRounded
            className="text-gray-400"
            size={24}
            onClick={toggleReport}
          />
          {reportVisible && (
            <div className="absolute flex items-center justify-center right-0 mt-2 w-20 px-2 bg-[#F16631] text-[#EF4444] bg-opacity-50 border border-gray-200 rounded-md shadow-lg z-10">
              <button
                className="px-4 py-2 text-sm w-full text-left"
                onClick={() => {
                  setReportFormVisible(true);
                  toggleReport();
                }}
              >
                Report
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="flex-grow" onClick={onClick}>
        <p className="text-gray-600 text-sm line-clamp-5  ">
          {reviewData?.description}
        </p>
      </div>
      <div className=" ">
        <div className="flex items-center  size-20 md:size-24 md:h-8 h-8">
          {[...Array(5)].map((_, index) => (
            <Star
              key={index}
              className={
                index < reviewData?.rating
                  ? "text-yellow-400 fill-current"
                  : "text-yellow-400"
              }
            />
          ))}
        </div>
        <p className="text-gray-400 text-xs mt-2">{formattedTimestamp}</p>
      </div>
      {reportFormVisible && (
        <Modal onClose={() => setReportFormVisible(false)}>
          <ReportForm />
        </Modal>
      )}
    </div>
  );
};

const ReviewDetail = ({ reviewData }) => {
  const formattedTimestamp = reviewData?.createdAt
    ? formatDistanceToNow(parseISO(reviewData?.createdAt), { addSuffix: true })
    : "A while ago";

  return (
    <div className="p-6  w-[80vw] md:w-[50vw] lg:w-[33vw]">
      <div className="flex items-start mb-4">
        {reviewData?.user?.picture ? (
          <img
            src={reviewData?.user?.picture}
            alt={reviewData?.user?.name}
            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-lg font-bold text-white">
            {reviewData?.user?.name?.charAt(0)}
          </div>
        )}
        <div className="ml-4 flex-grow">
          <h2 className="text-lg font-bold text-orange-600 truncate">
            {reviewData?.user?.name}
          </h2>
          <p className="text-gray-400 text-xs ">{formattedTimestamp}</p>
        </div>
        <div className=" ">
          <div className="flex items-center  size-20 md:size-24 md:h-8 h-8">
            {[...Array(5)].map((_, index) => (
              <Star
                key={index}
                className={
                  index < reviewData?.rating
                    ? "text-yellow-400 fill-current"
                    : "text-yellow-400"
                }
              />
            ))}
          </div>
        </div>
      </div>
      <div className="flex-grow">
        <p className="text-gray-600 text-sm">{reviewData?.description}</p>
      </div>
    </div>
  );
};
